import React, { useState, useEffect } from 'react';
import { FaTrophy, FaCalendarAlt, FaMapMarkerAlt } from 'react-icons/fa';
import { contentAPI } from '../services/api';
import { useLanguage } from '../context/LanguageContext';

const MatchCard = ({ initialMatch, allMatches, language, t, compact, formatDate, styles, index }) => {
  const [showRetour, setShowRetour] = useState(false);

  const retourMatch = allMatches ? allMatches.find(m =>
    m.id !== initialMatch.id && m.status === 'finished' &&
    ((m.home_team_id && initialMatch.away_team_id && m.home_team_id === initialMatch.away_team_id && m.away_team_id === initialMatch.home_team_id) ||
      (m.home_team_name && initialMatch.away_team_name && m.home_team_name === initialMatch.away_team_name && m.away_team_name === initialMatch.home_team_name))
  ) : null;

  const match = (showRetour && retourMatch) ? retourMatch : initialMatch;

  return (
    <div className="matchRow" style={{ ...styles.matchRow, ...(compact ? styles.compactMatchRow : {}), animationDelay: `${index * 0.1}s` }}>
      <div style={{ ...styles.teamsContainer, ...(compact ? styles.compactTeamsContainer : {}) }}>
        <div style={styles.teamSide}>
          <div style={{ ...styles.logoInfo, ...(compact ? styles.compactLogoInfo : {}) }}>
            {match.home_team_logo && (
              <img
                src={match.home_team_logo.startsWith('http') ? match.home_team_logo : (window.location.origin + match.home_team_logo)}
                alt={match.home_team_name}
                style={{ ...styles.teamLogo, ...(compact ? styles.compactTeamLogo : {}) }}
                onError={(e) => e.target.style.display = 'none'}
              />
            )}
            <span style={{ ...styles.teamName, ...(compact ? styles.compactTeamName : {}) }}>{match.home_team_name}</span>
          </div>
          {(match.yellow_cards_home > 0 || match.red_cards_home > 0) && (
            <div style={styles.sideCards}>
              {match.yellow_cards_home > 0 && <span style={styles.cardBadge}><div style={styles.yellowSquare}></div> {match.yellow_cards_home}</span>}
              {match.red_cards_home > 0 && <span style={styles.cardBadge}><div style={styles.redSquare}></div> {match.red_cards_home}</span>}
            </div>
          )}
        </div>

        <div style={styles.centerSection}>
          <div style={{ ...styles.scoreRow, ...(compact ? styles.compactScoreRow : {}) }}>
            <div style={{ ...styles.scoreVal, ...(compact ? styles.compactScoreVal : {}) }}>{match.home_score}</div>
            <div style={styles.scoreDash}>:</div>
            <div style={{ ...styles.scoreVal, ...(compact ? styles.compactScoreVal : {}) }}>{match.away_score}</div>
          </div>

          {retourMatch ? (
            <div style={styles.toggleContainer}>
              <button
                onClick={() => setShowRetour(false)}
                style={{ ...styles.toggleBtn, ...(!showRetour ? styles.toggleBtnActiveAller : {}) }}
              >
                {language === 'ar' ? 'نتيجة الذهاب' : 'Score Aller'}
              </button>
              <button
                onClick={() => setShowRetour(true)}
                style={{ ...styles.toggleBtn, ...(showRetour ? styles.toggleBtnActiveRetour : {}) }}
              >
                {language === 'ar' ? 'نتيجة الإياب' : 'Score Retour'}
              </button>
            </div>
          ) : (
            match.leg && (
              <div style={{ ...styles.matchLeg, background: match.leg === 'retour' ? '#fdecea' : '#eafdf0', color: match.leg === 'retour' ? '#d32f2f' : '#2e7d32' }}>
                {match.leg === 'retour' ? (language === 'ar' ? 'إياب' : 'Retour') : (language === 'ar' ? 'ذهاب' : 'Aller')}
              </div>
            )
          )}
        </div>

        <div style={{ ...styles.teamSide, flexDirection: 'row-reverse' }}>
          <div style={{ ...styles.logoInfo, flexDirection: 'row-reverse', ...(compact ? styles.compactLogoInfo : {}) }}>
            {match.away_team_logo && (
              <img
                src={match.away_team_logo.startsWith('http') ? match.away_team_logo : (window.location.origin + match.away_team_logo)}
                alt={match.away_team_name}
                style={{ ...styles.teamLogo, ...(compact ? styles.compactTeamLogo : {}) }}
                onError={(e) => e.target.style.display = 'none'}
              />
            )}
            <span style={{ ...styles.teamName, ...(compact ? styles.compactTeamName : {}), textAlign: 'right' }}>{match.away_team_name}</span>
          </div>
          {(match.yellow_cards_away > 0 || match.red_cards_away > 0) && (
            <div style={{ ...styles.sideCards, flexDirection: 'row-reverse' }}>
              {match.yellow_cards_away > 0 && <span style={styles.cardBadge}>{match.yellow_cards_away} <div style={styles.yellowSquare}></div></span>}
              {match.red_cards_away > 0 && <span style={styles.cardBadge}>{match.red_cards_away} <div style={styles.redSquare}></div></span>}
            </div>
          )}
        </div>
      </div>

      <div style={{ ...styles.matchFooter, ...(compact ? styles.compactMatchFooter : {}) }}>
        <div style={styles.footerItem}>
          <FaCalendarAlt size={compact ? 10 : 12} />
          <span>{formatDate(match.match_date)}</span>
        </div>
        {match.venue && (
          <div style={styles.footerItem}>
            <FaMapMarkerAlt size={compact ? 10 : 12} />
            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100px' }}>{match.venue}</span>
          </div>
        )}
      </div>
    </div>
  );
};

const ResultsDisplay = ({ matches, allMatches, round, title, compact = false }) => {
  const { language, t } = useLanguage();
  const [settings, setSettings] = useState({
    results_title: language === 'ar' ? 'نتائج الجولة الأخيرة' : 'Résultats de la dernière journée',
    results_subtitle: language === 'ar' ? 'فرق رابطة الهواة المستوى الثاني' : 'Ligue Amateur Niveau 2',
    current_season: '2025-2026',
    match_schedule: ''
  });

  useEffect(() => {
    loadSettings();
  }, [language]);

  const loadSettings = async () => {
    try {
      const response = await contentAPI.getSettings();
      const settingsData = response.data || response || [];
      const settingsMap = {};
      settingsData.forEach(setting => {
        settingsMap[setting.setting_key] = language === 'ar'
          ? (setting.setting_value_ar || setting.setting_value)
          : (setting.setting_value || setting.setting_value_ar);
      });
      setSettings(prev => ({ ...prev, ...settingsMap }));
    } catch (error) {
      console.error('Erreur chargement paramètres:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'ar' ? 'ar-TN' : 'fr-FR', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div style={{ ...styles.container, ...(compact ? styles.compactContainer : {}) }}>
      {/* Header avec logo */}
      {!compact && (
        <div style={styles.header}>
          <div style={styles.logoContainer}>
            <img src="/images/logo.png" alt="Logo" style={styles.logo} onError={(e) => e.target.src = '/logo512.png'} />
          </div>
          <div style={styles.headerContent}>
            <h2 style={styles.mainTitle}>{title || settings.results_title}</h2>
            <div style={styles.seasonBadge}>{t('season')} {settings.current_season}</div>
          </div>
        </div>
      )}

      {!compact && (
        <div style={styles.sectionTitle}>
          <FaTrophy style={styles.trophyIcon} />
          <span>{settings.results_subtitle}</span>
          <FaTrophy style={styles.trophyIcon} />
        </div>
      )}

      <div style={{ ...styles.matchesList, ...(compact ? styles.compactMatchesList : {}) }}>
        {matches.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px', color: 'rgba(255,255,255,0.7)' }}>{t('noData')}</div>
        ) : matches.map((match, index) => (
          <MatchCard
            key={match.id}
            initialMatch={match}
            allMatches={allMatches}
            language={language}
            t={t}
            compact={compact}
            formatDate={formatDate}
            styles={styles}
            index={index}
          />
        ))}
      </div>

      {!compact && matches.length === 0 && settings.match_schedule && (
        <div style={styles.footer}>
          <div style={styles.dateInfo}>{settings.match_schedule}</div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    background: 'linear-gradient(180deg, #1e3c72 0%, #2a5298 50%, #0a1929 100%)',
    borderRadius: '20px',
    padding: '40px',
    color: 'white',
    position: 'relative',
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
    width: '100%',
    boxSizing: 'border-box'
  },
  compactContainer: {
    padding: '8px',
    borderRadius: '10px',
    background: 'transparent',
    boxShadow: 'none'
  },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '30px', gap: '16px', flexWrap: 'wrap' },
  logoContainer: { background: 'white', borderRadius: '50%', padding: '8px', width: '80px', height: '80px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 15px rgba(0,0,0,0.3)' },
  logo: { width: '100%', height: '100%', maxWidth: '60px', maxHeight: '60px', objectFit: 'contain' },
  headerContent: { flex: 1, textAlign: 'center' },
  mainTitle: { fontSize: '32px', fontWeight: 'bold', marginBottom: '10px', textShadow: '2px 2px 4px rgba(0,0,0,0.5)' },
  seasonBadge: { display: 'inline-block', background: 'rgba(255,255,255,0.2)', padding: '8px 25px', borderRadius: '20px', fontSize: '18px', fontWeight: 'bold', backdropFilter: 'blur(10px)' },
  sectionTitle: { background: 'rgba(0,0,0,0.4)', padding: '20px', borderRadius: '50px', textAlign: 'center', fontSize: '24px', fontWeight: 'bold', marginBottom: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px', backdropFilter: 'blur(10px)', border: '2px solid rgba(255,255,255,0.1)' },
  trophyIcon: { fontSize: '28px', color: '#ffd700' },
  matchesList: { display: 'flex', flexDirection: 'column', gap: '15px' },
  compactMatchesList: { gap: '10px' },
  matchRow: { background: 'white', borderRadius: '20px', overflow: 'hidden', boxShadow: '0 8px 30px rgba(0,0,0,0.2)', animation: 'slideInUp 0.6s ease forwards', opacity: 0, transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', display: 'flex', flexDirection: 'column' },
  compactMatchRow: { borderRadius: '10px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' },
  teamsContainer: { padding: '25px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '20px', flexWrap: 'wrap' },
  compactTeamsContainer: { padding: '10px 12px', gap: '6px' },
  teamSide: { flex: 1, display: 'flex', flexDirection: 'column', gap: '5px', minWidth: '100px' },
  logoInfo: { display: 'flex', alignItems: 'center', gap: '10px' },
  compactLogoInfo: { gap: '5px' },
  teamLogo: { width: '45px', height: '45px', objectFit: 'contain', filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))' },
  compactTeamLogo: { width: '22px', height: '22px' },
  teamName: { fontSize: '18px', fontWeight: '800', color: '#1e293b', flex: 1, minWidth: '0', overflow: 'hidden', textOverflow: 'ellipsis' },
  compactTeamName: { fontSize: '12px', fontWeight: 'bold' },
  sideCards: { display: 'flex', gap: '4px', padding: '0 2px' },
  cardBadge: { display: 'flex', alignItems: 'center', gap: '3px', fontSize: '10px', fontWeight: 'bold', color: '#64748b', background: '#f1f5f9', padding: '1px 6px', borderRadius: '8px' },
  centerSection: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' },
  scoreRow: { display: 'flex', alignItems: 'center', gap: '12px', background: '#f1f5f9', padding: '10px 25px', borderRadius: '12px', border: '2px solid #e2e8f0', boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.05)' },
  compactScoreRow: { padding: '4px 8px', borderRadius: '6px', gap: '4px' },
  scoreVal: { fontSize: '32px', fontWeight: '900', color: '#c41e3a', minWidth: '35px', textAlign: 'center' },
  compactScoreVal: { fontSize: '15px', minWidth: '18px' },
  scoreDash: { fontSize: '24px', fontWeight: 'bold', color: '#94a3b8' },
  matchLeg: { fontSize: '10px', fontWeight: 'bold', padding: '2px 8px', borderRadius: '20px', textTransform: 'uppercase' },
  matchFooter: { background: '#f8fafc', padding: '10px 20px', display: 'flex', alignItems: 'center', gap: '15px', borderTop: '1px solid #f1f5f9', flexWrap: 'wrap' },
  compactMatchFooter: { display: 'none' }, // Masquer le footer en mode ultra-compact pour gagner de la place
  footerItem: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px', color: '#64748b', fontWeight: '500' },
  yellowSquare: { width: '6px', height: '9px', background: '#ffeb3b', borderRadius: '1px', border: '1px solid #c9bc1f' },
  redSquare: { width: '6px', height: '9px', background: '#f44336', borderRadius: '1px', border: '1px solid #b71c1c' },
  footer: { marginTop: '30px', padding: '15px', background: 'rgba(0,0,0,0.3)', borderRadius: '15px', textAlign: 'center', backdropFilter: 'blur(10px)' },
  dateInfo: { fontSize: '16px', fontWeight: 'bold', color: '#ffd700' },
  toggleContainer: { display: 'inline-flex', justifyContent: 'center', marginTop: '10px', overflow: 'hidden', borderRadius: '25px', boxShadow: '0 4px 10px rgba(0,0,0,0.15)', border: '3px solid #f1f5f9', background: '#f1f5f9' },
  toggleBtn: { padding: '6px 16px', fontSize: '12px', fontWeight: '900', border: 'none', cursor: 'pointer', transition: 'all 0.3s', textTransform: 'uppercase', background: 'transparent', color: '#64748b', borderRadius: '20px' },
  toggleBtnActiveAller: { background: '#1a365d', color: 'white', boxShadow: '0 2px 5px rgba(26,54,93,0.3)' },
  toggleBtnActiveRetour: { background: '#c41e3a', color: 'white', boxShadow: '0 2px 5px rgba(196,30,58,0.3)' },
};

// Ajouter l'animation
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes slideInUp {
    from { opacity: 0; transform: translateY(30px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .matchRow:hover { transform: translateY(-5px) !important; box-shadow: 0 8px 25px rgba(0,0,0,0.3) !important; }
`;
if (!document.querySelector('style[data-results-display]')) {
  styleSheet.setAttribute('data-results-display', 'true');
  document.head.appendChild(styleSheet);
}

export default ResultsDisplay;
